/**
 * What the app knows about ONE attachment before it tries to show it.
 *
 * Every list screen stores its attachments differently — an expense keeps a
 * mixed list of uploaded documents and filed receipts, a 529 distribution
 * keeps an inline documents array, a gift keeps bare receipt ids, a
 * beneficiary's term keeps school paperwork. The panel that shows them is one
 * component, so each screen normalises its own shape into the descriptor
 * below and nothing downstream has to know where it came from. (ZEN-351)
 *
 * The descriptor:
 *   key        stable identity, used for React's key AND for remounting the
 *              viewer when the reader switches between attachments
 *   label      the filename to show a person
 *   fileUrl    a ready-to-fetch URL for the file itself, or null when the
 *              record says an attachment exists but no file was ever stored
 *   receiptId  set when this attachment is a RECEIPT, which matters because
 *              the server can turn a receipt PDF into a picture page by page
 *              and cannot do that for anything else
 *   pageCount  how many pages that picture-by-picture view has, when known
 *   noFileNote why there is no file, in plain words, when fileUrl is null
 */

// The one place that decides how a file is shown, keyed on what the SERVER
// said the file is rather than on what the filename claims. A receipt saved as
// "statement" with no extension is still a picture, and a ".pdf" that is
// really an error page is not one.
export function displayKindFor(contentType) {
  const ct = (contentType || "").toLowerCase().split(";")[0].trim();
  if (!ct) return "unknown";
  if (ct === "application/pdf" || ct === "application/x-pdf") return "pdf";
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("text/")) return "text";
  return "unknown";
}

// Plain-English name for a file the browser cannot show, so the panel can say
// what it is holding instead of showing an empty box.
const FILE_KIND_WORDS = [
  [/spreadsheetml|ms-excel|^text\/csv$/, "a spreadsheet"],
  [/wordprocessingml|msword/, "a Word document"],
  [/^video\//, "a video"],
  [/^audio\//, "an audio recording"],
  [/zip|compressed/, "a compressed archive"],
];

export function describeUnshowableFile(contentType) {
  const ct = (contentType || "").toLowerCase();
  for (const [pattern, words] of FILE_KIND_WORDS) {
    if (pattern.test(ct)) return words;
  }
  return "a kind of file a browser cannot display";
}

/**
 * The accessible name of the paperclip button.
 *
 * It has to say BOTH what the button opens and which record it belongs to,
 * because on a list of forty rows every one of these buttons is otherwise
 * called the same thing, and somebody using a screen reader hears "button,
 * button, button" with no way to tell which expense they are on.
 */
export function attachmentButtonLabel(count, recordLabel, expanded) {
  const n = count || 0;
  const what = n === 1 ? "1 attachment" : `${n} attachments`;
  const verb = expanded ? "Hide" : "View";
  return recordLabel ? `${verb} ${what} for ${recordLabel}` : `${verb} ${what}`;
}

/**
 * The words for a row whose paperwork is missing. (ZEN-455, owner rulings
 * 2026-09-01 and 2026-09-02.)
 *
 * THERE IS NO "NO DOCUMENTS" LABEL, and that is a ruling, not an omission.
 * The first build put a quiet label on every empty row; the owner refused it
 * as clutter, and she was right on the evidence — a 529-eligible expense with
 * no paperwork is ALREADY announced twice, on the Overview ("N 529-eligible
 * expenses missing a receipt") and in the pre-print check on the audit packet
 * ("... has no attached receipt"). The absence of a paperclip is explained
 * once, in the help beside the list, instead of repeated on every row.
 *
 * WHY IT IS NOT ALARM-COLOURED EITHER. Red is this app's colour for a money
 * problem. This is a paperwork gap, which the Overview already colours amber,
 * so it matches — and the words name the small job to do rather than
 * announcing a loss. That distinction is load-bearing: a family who reads
 * "your file is gone" loses confidence in the whole record, and the app
 * cannot in fact lose one (there is no way to delete a receipt in the
 * product, and a beneficiary's own withdrawal is refused once the receipt is
 * attached to an expense). What this note actually reports is a LINK with
 * nothing behind it, which re-attaching closes.
 *
 * Real plurals, never "(s)": a screen reader speaks "(s)" literally.
 */
export function unavailableAttachmentsLabel(n) {
  return n === 1 ? "1 receipt needs re-attaching" : `${n} receipts need re-attaching`;
}

/** The screen-reader name, carrying which record it belongs to. */
export function attachmentGapAriaLabel(unavailable, recordLabel) {
  const base = unavailableAttachmentsLabel(unavailable);
  return recordLabel ? `${base} on ${recordLabel}` : base;
}

/**
 * The paperclip convention, explained ONCE for every screen that uses it.
 *
 * The owner asked for this after hitting the ambiguity herself: a row with no
 * paperclip could mean "nothing attached" or "no way to look". Saying it in
 * the help — rather than on every row — answers it without touching the list.
 *
 * `gapLine` is separate because only the expenses list can detect a broken
 * link. Gifts and 529 distributions hold their attachment ids on the record
 * itself, so promising them an amber note would promise something those
 * screens cannot do.
 */
/**
 * THE THIRD LINE SAYS "NO FILE TO OPEN", NOT "NOTHING IS ATTACHED", and the
 * difference is the whole reason this help is safe to state unconditionally.
 *
 * The screens ship ahead of the server here — the front end deploys on merge
 * while a server release waits for its click — so there is a real window in
 * which the list renders from a response that has no unavailable count in it
 * at all. A row with a broken link looks empty in that window (the reader has
 * always dropped links it cannot resolve), and the amber note correctly stays
 * silent rather than guess. "Nothing is attached yet" would then be a flat
 * lie about somebody's evidence; "no file to open" stays true in every state,
 * because a link with nothing behind it is exactly a row with no file to
 * open. It still answers the question the owner actually asked — is this row
 * empty, or is there no way to look? (Merge-gate review, round 5.)
 */
export const PAPERCLIP_HELP = [
  "A paperclip means a file is attached. Click it to view the file just below the row.",
  "When more than one file is attached, the count sits beside the paperclip.",
  "No paperclip means there is no file to open.",
];

export const PAPERCLIP_HELP_GAP_LINE =
  "An amber note appears instead when an expense lists a receipt that isn't in your records, so you can attach it again.";
